import React, { useState } from "react";
import { 
  ArrowLeft, Leaf, ChevronRight, Fuel, AlertTriangle, UserX, 
  Receipt, Wrench, Check, Gauge, Monitor, Cog, TreePine, Shield,
  ClipboardList, MessageSquare, RefreshCw, Brain
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CuestionarioDiagnostico = ({ clientData, respuestas, setRespuestas, onContinue, onBack, areasOverride, embedded = false }) => {
  const [activeArea, setActiveArea] = useState(0);
  const [generando, setGenerando] = useState(false);

  const areasDefault = [
    {
      id: 'seguridad',
      nombre: 'Seguridad y Riesgo',
      icono: <Shield className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'seg_1', texto: '¿Tenéis diferencias claras de estilo de conducción entre conductores?', opciones: ['No lo sabemos', 'Sí, pero sin datos', 'Sí, con datos objetivos'] },
        { id: 'seg_2', texto: '¿Sabéis quién genera más riesgo potencial?', opciones: ['No', 'Intuimos', 'Sí, identificados'] },
        { id: 'seg_3', texto: 'Cuando hay un accidente, ¿tenéis datos objetivos o versión del conductor?', opciones: ['Solo versión conductor', 'Algunos datos parciales', 'Datos objetivos completos'] },
        { id: 'seg_4', texto: '¿La aseguradora os ha pedido medidas concretas de mejora?', opciones: ['No', 'Sí, genéricas', 'Sí, específicas'] },
        { id: 'seg_5', texto: '¿Podríais defenderos técnicamente en un procedimiento judicial?', opciones: ['Difícilmente', 'Con dificultad', 'Sí, con evidencias'] }
      ]
    },
    {
      id: 'combustible',
      nombre: 'Combustible',
      icono: <Fuel className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'fuel_1', texto: '¿Qué diferencia de consumo hay entre vuestro mejor y peor conductor?', opciones: ['No lo sabemos', 'Menos del 10%', '10-20%', 'Más del 20%'] },
        { id: 'fuel_2', texto: '¿Controláis el ralentí de forma sistemática?', opciones: ['No', 'Parcialmente', 'Sí, con alertas'] },
        { id: 'fuel_3', texto: '¿Tenéis identificado dónde se producen los excesos de velocidad?', opciones: ['No', 'Zonas generales', 'Puntos exactos'] },
        { id: 'fuel_4', texto: '¿Habéis cuantificado cuánto os cuesta el estilo de conducción?', opciones: ['No', 'Estimación', 'Cálculo preciso'] }
      ]
    },
    {
      id: 'operativa',
      nombre: 'Operativa y Control',
      icono: <Gauge className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'oper_1', texto: '¿Tenéis visibilidad en tiempo real o análisis posterior?', opciones: ['Sin visibilidad', 'Solo posterior', 'Tiempo real'] },
        { id: 'oper_2', texto: '¿Sabéis cuánto tiempo improductivo tiene cada vehículo?', opciones: ['No', 'Aproximado', 'Dato exacto'] },
        { id: 'oper_3', texto: '¿Podéis comparar rutas planificadas vs reales?', opciones: ['No', 'Manualmente', 'Automáticamente'] },
        { id: 'oper_4', texto: '¿Detectáis usos fuera de horario automáticamente o por casualidad?', opciones: ['Por casualidad', 'Revisión manual', 'Alertas automáticas'] }
      ]
    },
    {
      id: 'mantenimiento',
      nombre: 'Mantenimiento',
      icono: <Wrench className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'mant_1', texto: '¿El mantenimiento es preventivo o reactivo?', opciones: ['Reactivo', 'Mixto', 'Preventivo planificado'] },
        { id: 'mant_2', texto: '¿Tenéis alertas automáticas de fallos motor (DTC)?', opciones: ['No', 'Del taller', 'Sistema propio'] },
        { id: 'mant_3', texto: '¿Aprovecháis todos los mantenimientos incluidos en renting?', opciones: ['No lo sabemos', 'Probablemente no todos', 'Sí, controlado'] },
        { id: 'mant_4', texto: '¿Tenéis desviaciones grandes de kilómetros entre vehículos similares?', opciones: ['No lo sabemos', 'Sí, sin control', 'Controlado'] },
        { id: 'mant_5', texto: '¿Os ha ocurrido una avería grave que podría haberse anticipado?', opciones: ['Sí, varias', 'Alguna vez', 'No / Muy raro'] }
      ]
    },
    {
      id: 'tic',
      nombre: 'TIC / Digitalización',
      icono: <Monitor className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'tic_1', texto: '¿Tenéis un único sistema o varios que no se hablan entre sí?', opciones: ['Varios sin integrar', 'Parcialmente integrados', 'Sistema único'] },
        { id: 'tic_2', texto: '¿Los datos están integrados con vuestros sistemas internos?', opciones: ['No', 'Parcialmente', 'Totalmente'] },
        { id: 'tic_3', texto: '¿El responsable de flota dedica mucho tiempo administrativo?', opciones: ['Sí, excesivo', 'Bastante', 'Optimizado'] },
        { id: 'tic_4', texto: '¿Tenéis KPIs automáticos o informes manuales?', opciones: ['Manuales', 'Semi-automáticos', 'Automáticos'] },
        { id: 'tic_5', texto: '¿Dependéis del conductor para saber lo que ocurre?', opciones: ['Totalmente', 'Parcialmente', 'No, datos objetivos'] }
      ]
    },
    {
      id: 'sostenibilidad',
      nombre: 'Sostenibilidad / ESG',
      icono: <TreePine className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      preguntas: [
        { id: 'esg_1', texto: '¿Os han pedido reducir emisiones de CO₂?', opciones: ['No', 'Internamente', 'Clientes/Regulador'] },
        { id: 'esg_2', texto: '¿Tenéis datos reales para decidir electrificación?', opciones: ['No', 'Parciales', 'Completos'] },
        { id: 'esg_3', texto: '¿Sabéis qué parte de vuestra flota es electrificable hoy?', opciones: ['No', 'Aproximado', 'Análisis detallado'] }
      ]
    }
  ];

  // Si llega override desde BD, usarlo. Si no, defaults hardcodeados (flujo público).
  const areas = (areasOverride && areasOverride.length > 0) ? areasOverride : areasDefault;

  const handleRespuesta = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: { ...prev[preguntaId], valor }
    }));
  };

  const handleNota = (preguntaId, nota) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: { ...prev[preguntaId], nota }
    }));
  };

  const getAreaProgress = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    if (!area) return 0;
    const respondidas = area.preguntas.filter(p => respuestas[p.id]?.valor).length;
    return Math.round((respondidas / area.preguntas.length) * 100);
  };

  const handleContinuar = async () => {
    // Verificar si hay respuestas
    const totalRespuestas = Object.values(respuestas).filter(r => r?.valor).length;
    
    if (totalRespuestas === 0) {
      // Sin respuestas, pasar directo sin diagnóstico
      onContinue(null);
      return;
    }

    setGenerando(true);
    try {
      const response = await axios.post(`${API}/ia/generar-diagnostico`, {
        empresa: clientData?.empresa || '',
        sector: clientData?.sector || '',
        respuestas: respuestas
      });
      
      // El backend devuelve { diagnostico: {...}, resumen: "..." }
      // Transformar al formato que espera el Simulator: { problemas: {...}, resumen: "..." }
      const diagData = {
        problemas: response.data.diagnostico || {},
        resumen: response.data.resumen || ''
      };
      
      toast.success('Diagnóstico generado con IA');
      onContinue(diagData);
    } catch (error) {
      console.error('Error generando diagnóstico:', error);
      toast.error('No se pudo generar el diagnóstico IA. Continuando sin él.');
      onContinue(null);
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-gradient-to-br from-emerald-50 to-green-50"}>
      {/* Header - mismo estilo que Simulator (oculto en modo embebido · viene del stepper exterior) */}
      {!embedded && (
      <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 text-white py-4 shadow-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-lg transition-all" data-testid="back-from-diagnostico-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img
              src="/logo-mobilsafe.png"
              alt="MOBILSAFE"
              className="h-9 md:h-10 bg-white/95 rounded px-2 py-1"
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-9 h-9 bg-white/15 backdrop-blur rounded-lg flex items-center justify-center border border-white/30">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <p className="text-base font-black tracking-tight text-white">MOBILSAFE</p>
                <p className="text-[9px] text-emerald-100 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
              </div>
            </div>
            <div className="border-l border-white/30 pl-4">
              <h1 className="text-xl font-black">Diagnóstico: {clientData?.empresa}</h1>
              <p className="text-emerald-200 text-sm">Análisis Consultivo • Paso 1 de 3</p>
            </div>
          </div>
          <button onClick={handleContinuar} disabled={generando} className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg font-bold shadow-lg transition-all flex items-center gap-2 disabled:opacity-50" data-testid="skip-diagnostico-btn">
            {generando ? (
              <><RefreshCw className="w-5 h-5 animate-spin" /> Generando diagnóstico IA...</>
            ) : (
              <><Brain className="w-5 h-5" /> Generar Diagnóstico y Continuar</>
            )}
          </button>
        </div>
      </div>
      )}

      <div className={embedded ? "px-2 py-3" : "max-w-7xl mx-auto px-6 py-8"}>
        {/* Tarjeta de cliente - oculta en modo embebido */}
        {!embedded && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-emerald-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                <ClipboardList className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-800">Identificamos tus áreas de mejora</h2>
                <p className="text-gray-600">Responde lo que sepas. Puedes saltar áreas o preguntas.</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Progress de áreas */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border-2 border-emerald-200">
          <div className="flex justify-between items-center overflow-x-auto pb-2">
            {areas.map((area, idx) => (
              <button
                key={area.id}
                onClick={() => setActiveArea(idx)}
                className={`flex flex-col items-center gap-2 px-4 py-3 rounded-xl transition-all min-w-[120px] ${activeArea === idx ? 'bg-emerald-50 border-2 border-emerald-400 scale-105' : 'hover:bg-gray-50'}`}
                data-testid={`area-btn-${area.id}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${activeArea === idx ? 'bg-gradient-to-br from-emerald-500 to-green-500 text-white shadow-lg' : 'bg-emerald-100 text-emerald-600'}`}>
                  {area.icono}
                </div>
                <span className={`text-xs font-bold text-center ${activeArea === idx ? 'text-emerald-700' : 'text-gray-600'}`}>{area.nombre}</span>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${getAreaProgress(area.id)}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Contenido del área activa */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-emerald-200">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-emerald-100">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg text-white">
              {areas[activeArea].icono}
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-800">{areas[activeArea].nombre}</h3>
              <p className="text-sm text-gray-500">{areas[activeArea].preguntas.length} preguntas • {getAreaProgress(areas[activeArea].id)}% completado</p>
            </div>
          </div>

          <div className="space-y-6">
            {areas[activeArea].preguntas.map((pregunta, idx) => (
              <div key={pregunta.id} className="bg-emerald-50/50 rounded-xl p-5 border border-emerald-200">
                <p className="font-semibold mb-4 flex items-start gap-2 text-gray-800">
                  <span className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">{idx + 1}</span>
                  {pregunta.texto}
                </p>
                
                {/* Opciones */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {pregunta.opciones.map((opcion, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleRespuesta(pregunta.id, opcion)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        respuestas[pregunta.id]?.valor === opcion
                          ? 'bg-emerald-500 text-white shadow-md'
                          : 'bg-white border-2 border-emerald-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50'
                      }`}
                      data-testid={`opcion-${pregunta.id}-${optIdx}`}
                    >
                      {opcion}
                    </button>
                  ))}
                </div>

                {/* Campo de notas */}
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-emerald-500 mt-2 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Notas adicionales (opcional)..."
                    value={respuestas[pregunta.id]?.nota || ''}
                    onChange={(e) => handleNota(pregunta.id, e.target.value)}
                    className="flex-1 px-3 py-2 bg-white border-2 border-emerald-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
                    data-testid={`nota-${pregunta.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navegación */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setActiveArea(Math.max(0, activeArea - 1))}
            disabled={activeArea === 0}
            className="px-5 py-2.5 bg-white border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Área anterior
          </button>

          <div className="flex gap-2">
            {areas.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveArea(idx)}
                className={`w-3 h-3 rounded-full transition-all ${idx === activeArea ? 'bg-emerald-500 w-8' : 'bg-emerald-200 hover:bg-emerald-300'}`}
              />
            ))}
          </div>

          {activeArea < areas.length - 1 ? (
            <button
              onClick={() => setActiveArea(activeArea + 1)}
              className="px-5 py-2.5 bg-white border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              Siguiente área <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            !embedded && (
            <button
              onClick={handleContinuar}
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg"
              data-testid="finalizar-diagnostico-btn"
            >
              Continuar al Simulador <ChevronRight className="w-5 h-5" />
            </button>
            )
          )}
        </div>
      </div>

      {/* Footer con enlaces legales · oculto en modo embebido */}
      {!embedded && (
      <footer className="py-6 border-t border-emerald-200 mt-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-2 text-sm">
            <a 
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-600/60 hover:text-emerald-600 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-emerald-600/30">|</span>
            <a 
              href="https://www.mobilsafe.es/politica-de-cookies/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-600/60 hover:text-emerald-600 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <p className="text-emerald-600/40 text-xs">© 2026 MOBILSAFE Fleet Technology</p>
        </div>
      </footer>
      )}
    </div>
  );
};

export default CuestionarioDiagnostico;
